import {
  Box,
  Flex,
  FlexProps,
  Collapse,
  useDisclosure,
  useColorModeValue,
  Text,
  Badge,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { LucideIcon, ChevronDown, ChevronRight } from 'lucide-react';
import { IconButton } from 'src/components/atoms/IconButton';
import { MMMRoute } from 'src/app/routes/utils';
import { useActiveRoute } from 'src/hooks/useActiveRoute';
import { Link } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';

type SidebarCollapsibleItemProps = FlexProps & {
  icon: LucideIcon;
  route: MMMRoute;
  isActive?: boolean;
  children?: React.ReactNode;
  badgeCount?: number;
  onNavigate?: () => void;
};

export const SidebarCollapsibleItem = ({
  icon: IconComponent,
  route,
  isActive = false,
  children,
  badgeCount,
  onNavigate,
  ...rest
}: SidebarCollapsibleItemProps) => {
  const { activeSubroute } = useActiveRoute();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isActive });

  const activeBg = 'sidebar.itemActiveBg';
  const activeColor = 'sidebar.accent';
  const textColor = 'sidebar.text';
  const subrouteHoverBg = 'sidebar.itemHoverBg';
  const subrouteActiveBg = 'sidebar.itemActiveBg';

  const hasSubroutes = route.subroutes && route.subroutes.length > 0;

  // Beta badge
  const betaBg = useColorModeValue('blue.100', 'blue.900');
  const betaColor = useColorModeValue('blue.700', 'blue.300');

  return (
    <Box mt="1">
      <Flex
        align="center"
        mx="4"
        px="3"
        py="2"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        onClick={hasSubroutes ? onToggle : undefined}
        bg={isActive && !hasSubroutes ? activeBg : 'transparent'}
        _hover={{ bg: 'sidebar.itemHoverBg' }}
        color={isActive ? activeColor : textColor}
        {...rest}>
        {IconComponent && (
          <ChakraIcon
            as={IconComponent}
            mr="3"
            w={5}
            h={5}
            color={isActive ? 'sidebar.accent' : 'sidebar.textMuted'}
          />
        )}
        <Link
          as={NavLink}
          to={route.url}
          flex="1"
          style={{ textDecoration: 'none' }}
          onClick={onNavigate}
          _activeLink={{
            color: activeColor,
          }}>
          <Flex align="center" gap={4}>
            <Flex align="center" gap={2} flex="1" minW={0}>
              <Text
                fontFamily="marketingPage"
                fontSize={15}
                minW={0}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                color={isActive ? 'sidebar.textActive' : 'sidebar.text'}
                fontWeight={'medium'}>
                {route.name}
              </Text>
              {route.isBeta && (
                <Badge
                  bg={betaBg}
                  color={betaColor}
                  borderRadius="md"
                  px={2}
                  py={0.5}
                  fontSize="10px"
                  fontWeight="semibold"
                  fontFamily="marketingPage"
                  textTransform="none"
                  flexShrink={0}>
                  Beta
                </Badge>
              )}
            </Flex>
            {badgeCount !== undefined && badgeCount > 0 && (
              <Badge
                bg="sidebar.accent"
                color="white"
                borderRadius="full"
                w="22px"
                h="22px"
                minW="22px"
                p={0}
                fontSize="10px"
                fontWeight="normal"
                fontFamily="marketingPage"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}>
                {badgeCount > 99 ? '99+' : badgeCount}
              </Badge>
            )}
          </Flex>
        </Link>
        {hasSubroutes && (
          <IconButton
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            icon={
              <ChakraIcon
                as={isOpen ? ChevronDown : ChevronRight}
                boxSize="5"
              />
            }
            variant="unstyled"
            size="sm"
            minW="auto"
            h="auto"
            display="flex"
            alignItems="center"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
        )}
      </Flex>

      {hasSubroutes && (
        <Collapse in={isOpen} animateOpacity>
          {/* 38px = row margin (16) + padding (12) + half the 20px icon,
              so the guide line runs under the parent icon's center */}
          <Box
            ml="38px"
            borderLeft="1px solid"
            borderLeftColor="surface.border">
            {route.subroutes?.map((subroute) => {
              const isSubrouteActive = activeSubroute?.url === subroute.url;

              return (
                <Box key={subroute.url} pl="2">
                  <NavLink
                    to={subroute.url}
                    style={{ textDecoration: 'none' }}
                    onClick={onNavigate}>
                    <Flex
                      align="center"
                      py="2"
                      pl="4"
                      pr="3"
                      mr="4"
                      borderRadius="lg"
                      bg={isSubrouteActive ? subrouteActiveBg : 'transparent'}
                      _hover={{
                        bg: subrouteHoverBg,
                        color: textColor,
                      }}
                      color={isSubrouteActive ? activeColor : textColor}>
                      <Text
                        fontFamily="marketingPage"
                        fontSize={15}
                        flex="1"
                        color={
                          isSubrouteActive
                            ? 'sidebar.textActive'
                            : 'sidebar.text'
                        }
                        fontWeight={'medium'}>
                        {subroute.name}
                      </Text>
                    </Flex>
                  </NavLink>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
